<template>
  <ELYXModalBox>
    <ELYXModalHeader>{{ getHeaderText }}</ELYXModalHeader>

    <!-- SUSPENDED -->
    <div v-if="bookingSuspendedByAdmin || bookingSuspendedCancellationAbuse">
      <ELYXModalText v-if="bookingSuspendedByAdmin">
        Unfortunately, you've not followed Cofocus guidelines and we've had to suspend your account from booking more sessions.
      </ELYXModalText>
      <ELYXModalText v-else-if="bookingSuspendedCancellationAbuse">
        Unfortunately, you've not followed Cofocus guidelines by cancelling at least 3 ongoing sessions in the last 7 days while your partner was still in
        the session. 
        <br />
        <br />
        As a consequence, we've had to suspend your account from booking more sessions.
      </ELYXModalText>

      <ELYXModalText>Please contact support@cofocus.one for appeals.</ELYXModalText>
    </div>

    <!-- NEW USER LIMIT -->
    <div v-else-if="userHasNotCompletedFirstSession">
      <ELYXModalText> Successfully complete your first session to unlock being able to book more sessions at a time. </ELYXModalText>
      <ELYXModalText> Happy sessioning! </ELYXModalText>
    </div>

    <!-- BOOKING DISABLED-->
    <div v-else-if="bookingDisabled">
      <ELYXModalText>
        Unfortunately, your attendance of sessions has not met the minimum requirements of Cofocus and therefore we've had to limit your account from
        booking more sessions.
      </ELYXModalText>

      <ELYXModalText> Please contact support@cofocus.one to appeal and re-enable booking sessions for your account. </ELYXModalText>
    </div>

    <!-- REGULAR BOOKING LIMIT REACHED -->
    <div v-else>
      <ELYXModalText>
        <!-- @note: selected date times is +1 because we prevent adding new selection, thus latest selection attempt is unreflected in variable -->
        You attempted to book <span class="CalendarBookingRestrictionModal--number">{{ selectedDateTimes.length + 1 }}</span> new
        {{ selectedDateTimes.length + 1 === 1 ? "session" : "sessions" }}, and you already have
        <span class="CalendarBookingRestrictionModal--number">{{ bookedCount }}</span> {{ bookedCount === 1 ? "session" : "sessions" }} booked.
        However, your current booking limit is <span class="CalendarBookingRestrictionModal--number">{{ bookingLimit }}</span
        >.
      </ELYXModalText>

      <ELYXModalText v-if="sessionsUntilUnlimitedBooking">
        Successfully complete <span class="CalendarBookingRestrictionModal--number">{{ sessionsUntilUnlimitedBooking }}</span>
        {{ sessionsUntilUnlimitedBooking === 1 ? "session" : "sessions" }} to book without limitations!
      </ELYXModalText>

      <!-- Marking exact number of sessions left is difficult for <20 sessions, because we have Levels/XP that affect booking limit too,
          and also due to more custom calculations for those numbers -->
      <ELYXModalText v-else> Successfully complete more sessions and you'll be on your way to book without limitations! </ELYXModalText>

      <ELYXModalText> Alternatively, you can also cancel existing sessions. </ELYXModalText>
    </div>

    <ELYXModalButton @click="closeModal">Continue</ELYXModalButton>

    <ELYXModalSubtext v-if="userHasNotCompletedFirstSession">
      Sessions are considered successful when joined within the first 3 minutes and attended for a total of 47 minutes.
    </ELYXModalSubtext>

    <ELYXModalTextLink
      v-else-if="!bookingSuspendedByAdmin && !bookingSuspendedCancellationAbuse && !bookingDisabled && !userHasNotCompletedFirstSession"
      :isButtonLink="true"
      @click="toggleBookingLimitInfoModal"
    >
      Learn more about booking limits.
    </ELYXModalTextLink>
  </ELYXModalBox>
</template>

<script>
import { mapState } from "vuex";
import { CalendarStore, ELYXCentralStore } from "../../ELYX/ELYXStore";
import ELYXConstants from "../../ELYX/ELYXConstants";
import ELYXModalBox from "../../ELYXComponents/ELYXModalBox.vue";
import ELYXModalHeader from "../../ELYXComponents/ELYXModalHeader.vue";
import ELYXModalText from "../../ELYXComponents/ELYXModalText.vue";
import ELYXModalSubtext from "../../ELYXComponents/ELYXModalSubtext.vue";
import ELYXModalButton from "../../ELYXComponents/ELYXModalButton.vue";
import ELYXModalTextLink from "../../ELYXComponents/ELYXModalTextLink.vue";
import { SharedSessionUtilities } from "@shared";

export default {
  components: {
    ELYXModalBox,
    ELYXModalHeader,
    ELYXModalText,
    ELYXModalSubtext,
    ELYXModalButton,
    ELYXModalTextLink,
  },
  computed: {
    ...mapState({
      selectedDateTimes: (state) => state.selections.selectedDateTimes,
    }),

    sessionCount: () => CalendarStore.state.sessionCount,
    bookedCount: () => CalendarStore.state.bookedCount,
    bookingLimit: () => CalendarStore.state.bookingLimit,
    bookingSuspendedByAdmin: () => CalendarStore.state.bookingSuspendedByAdmin,
    bookingSuspendedCancellationAbuse: () => CalendarStore.state.bookingSuspendedCancellationAbuse,
    attemptingToBookCount: () => CalendarStore.state.attemptingToBookCount,
    sessionsUntilUnlimitedBooking: () => CalendarStore.state.sessionsUntilUnlimitedBooking,

    bookingDisabled() {
      return SharedSessionUtilities.isUserBookingDisabled(this.bookingLimit);
    },

    userHasNotCompletedFirstSession() {
      const attemptingToBookCount = this.selectedDateTimes.length + 1;
      return SharedSessionUtilities.isUserBookingWithoutCompletingFirstSession(this.bookedCount, this.sessionCount, attemptingToBookCount);
    },

    getHeaderText() {
      if (this.bookingSuspendedByAdmin || this.bookingSuspendedCancellationAbuse) {
        return "Account Suspended!";
      } else if (this.userHasNotCompletedFirstSession) {
        return "One step at a time :)";
      } else if (this.bookingDisabled) {
        return "Booking Disabled!";
      } else {
        return "Booking Limit Reached!";
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit("closeModal");
    },
    toggleBookingLimitInfoModal() {
      ELYXCentralStore.methods.setModal(ELYXConstants.modals.CalendarBookingLimitInfoModal);
    },
  },
};
</script>

<style scoped>
.CalendarBookingRestrictionModal--number {
  font-size: 17px;
  font-weight: 500;
}
</style>
